import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import { createWorker } from "tesseract.js";

/* ---------- Helper functions ---------- */
/* createImage: load image */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.setAttribute("crossOrigin", "anonymous");
    image.onload = () => resolve(image);
    image.onerror = (e) => reject(e);
    image.src = url;
  });

/* getRadianAngle */
const getRadianAngle = (degreeValue) => (degreeValue * Math.PI) / 180;

/* rotateSize - calculate bounding box after rotation */
const rotateSize = (width, height, rotation) => {
  const rot = Math.abs(rotation) % 180;
  const rad = getRadianAngle(rot);
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
};

/* getCroppedImg: returns base64 of cropped area applying rotation & flips */
async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false }
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // calculate bounding box of the rotated image
  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas to bounding box size
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas to center to rotate around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Now extract the cropped area from the rotated image
  // Make sure pixelCrop values are integers and within bounds
  const sx = Math.max(0, Math.floor(pixelCrop.x));
  const sy = Math.max(0, Math.floor(pixelCrop.y));
  const sw = Math.max(1, Math.floor(pixelCrop.width));
  const sh = Math.max(1, Math.floor(pixelCrop.height));

  // Clamp width/height to canvas dimensions
  const clampedW = Math.min(sw, canvas.width - sx);
  const clampedH = Math.min(sh, canvas.height - sy);

  const data = ctx.getImageData(sx, sy, clampedW, clampedH);

  // create a new canvas for the final crop
  const croppedCanvas = document.createElement("canvas");
  croppedCanvas.width = clampedW;
  croppedCanvas.height = clampedH;
  const croppedCtx = croppedCanvas.getContext("2d");
  croppedCtx.putImageData(data, 0, 0);

  // return base64
  return croppedCanvas.toDataURL("image/jpeg");
}

/* ---------- Component ---------- */
const ImageCropperFile = ({
  base64Value,
  setBase64Value,
  id = "image-uploader",
}) => {
  const [imagePreview, setImagePreview] = useState(base64Value || "");
  const [imageForCrop, setImageForCrop] = useState(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });

  const [aspect, setAspect] = useState(null); // null = free
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCrop, setShowCrop] = useState(false);

  const [loading, setLoading] = useState(false);
  const workerRef = useRef(null);

  // (OCR helper commented out — enable if you want to run OCR here)
  // const extractTextFromFile = async (file) => { ... };

  /* Drop handler */
  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = () => {
      setImageForCrop(reader.result);
      setShowCrop(true);
      // reset controls
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setRotation(0);
      setFlip({ horizontal: false, vertical: false });
      setAspect(null);
      setCroppedAreaPixels(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  /* When crop completes we get pixel area */
  const onCropComplete = useCallback((croppedArea, croppedAreaPixelsArg) => {
    setCroppedAreaPixels(croppedAreaPixelsArg);
  }, []);

  /* Apply crop: generate base64, run OCR (optional), call setBase64Value */
  const applyCrop = async () => {
    if (!imageForCrop || !croppedAreaPixels) return;
    try {
      setLoading(true);

      // Here we pass the pixel crop & transforms to helper
      const base64 = await getCroppedImg(
        imageForCrop,
        croppedAreaPixels,
        rotation,
        flip
      );

      // set preview immediately
      setImagePreview(base64);
      setShowCrop(false);

      // convert base64 to blob/file for OCR if needed
      const res = await fetch(base64);
      const blob = await res.blob();
      const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
      // const text = await extractTextFromFile(file);

      // call parent setter with (base64, text-or-empty)
      if (typeof setBase64Value === "function") setBase64Value(base64, file);
    } catch (err) {
      console.error("applyCrop error:", err);
    } finally {
      setLoading(false);
    }
  };

  const removeImage = () => {
    setImagePreview("");
    setImageForCrop(null);
    setCroppedAreaPixels(null);
    if (typeof setBase64Value === "function") setBase64Value("", "");
  };

  /* UI helpers */
  const toggleFlipH = () =>
    setFlip((f) => ({ ...f, horizontal: !f.horizontal }));
  const toggleFlipV = () => setFlip((f) => ({ ...f, vertical: !f.vertical }));
  const rotate90 = () => setRotation((r) => (r + 90) % 360);

  return (
    <div className="container mt-5" id={id}>
      <div
        {...getRootProps()}
        className="border border-primary rounded p-3 text-center"
        style={{ borderStyle: "dashed", cursor: "pointer" }}
      >
        <input {...getInputProps()} />
        <div className="d-flex align-items-center justify-content-center">
          {imagePreview ? (
            <div className="position-relative">
              <img
                src={imagePreview}
                alt="preview"
                className="img-fluid rounded shadow-sm"
                style={{ maxWidth: 120, maxHeight: 120, objectFit: "contain" }}
              />
              <button
                onClick={removeImage}
                type="button"
                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                aria-label="Remove image"
              >
                ✖
              </button>
            </div>
          ) : (
            <div>
              <p className="mb-0 text-muted">
                Drag & Drop an image or Click to Upload
              </p>
              <small className="text-muted">(will open crop modal)</small>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Custom Bootstrap-style Modal (inside component) ---------- */}
      {showCrop && (
        <div
          className="fixed-top d-flex align-items-center justify-content-center"
          style={{
            zIndex: 2000,
            height: "100vh",
            background: "rgba(0,0,0,0.65)",
            padding: "1.5rem",
            overflow: "auto",
          }}
        >
          <div
            className="bg-white rounded shadow-lg"
            style={{
              width: "100%",
              maxWidth: 980,
              minHeight: 420,
              position: "relative",
            }}
          >
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 className="mb-0">Crop Image</h5>

              <div>
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => {
                    setShowCrop(false);
                    setImageForCrop(null);
                  }}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={applyCrop}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Processing...
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
            </div>

            <div
              className="d-flex flex-column flex-md-row"
              style={{ minHeight: 360 }}
            >
              {/* Crop area */}
              <div
                className="flex-fill position-relative bg-dark"
                style={{
                  minHeight: 360,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* The cropper sits in its own container. Controls are separate and clickable. */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: 360,
                    maxWidth: 640,
                    background: "#111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Pass 'aspect' only when it's a number; omit for free */}
                  <Cropper
                    image={imageForCrop}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    {...(typeof aspect === "number" ? { aspect } : {})}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onRotationChange={(r) => setRotation(r)}
                    onCropComplete={onCropComplete}
                    showGrid={false}
                  />

                  {/* Instagram-style overlay corners & handles (visual only) */}
                  <div
                    style={{
                      pointerEvents: "none",
                      position: "absolute",
                      inset: 0,
                    }}
                    aria-hidden
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0,0,0,0.45)",
                        borderRadius: 6,
                      }}
                    />
                    {/* Centered white frame */}
                    <div
                      style={{
                        pointerEvents: "none",
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%,-50%)",
                        width: "65%",
                        height: "65%",
                        border: "2px solid rgba(255,255,255,0.95)",
                        borderRadius: 8,
                        boxShadow: "0 4px 18px rgba(0,0,0,0.6)",
                      }}
                    >
                      {/* 8 round handles (visual only) */}
                      {["tl", "t", "tr", "r", "br", "b", "bl", "l"].map(
                        (pos) => {
                          const common = {
                            width: 14,
                            height: 14,
                            background: "white",
                            borderRadius: "50%",
                            position: "absolute",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                          };
                          const positions = {
                            tl: { left: -7, top: -7 },
                            t: {
                              left: "50%",
                              top: -7,
                              transform: "translateX(-50%)",
                            },
                            tr: { right: -7, top: -7 },
                            r: {
                              right: -7,
                              top: "50%",
                              transform: "translateY(-50%)",
                            },
                            br: { right: -7, bottom: -7 },
                            b: {
                              left: "50%",
                              bottom: -7,
                              transform: "translateX(-50%)",
                            },
                            bl: { left: -7, bottom: -7 },
                            l: {
                              left: -7,
                              top: "50%",
                              transform: "translateY(-50%)",
                            },
                          };
                          return (
                            <div
                              key={pos}
                              style={{ ...common, ...positions[pos] }}
                            />
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls area */}
              <div
                className="p-3 border-start"
                style={{
                  width: 340,
                  background: "#fff",
                  zIndex: 5,
                  pointerEvents: "auto", // ensures buttons clickable
                }}
              >
                {/* Aspect ratio */}
                <div className="mb-3">
                  <label className="form-label mb-1">Aspect</label>
                  <div
                    className="btn-group w-100"
                    role="group"
                    aria-label="Aspect ratio"
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        aspect === null ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() => setAspect(null)}
                    >
                      Free
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        aspect === 1 ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() => setAspect(1)}
                    >
                      1:1
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        aspect === 3 / 4 ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() => setAspect(3 / 4)}
                    >
                      3:4
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        aspect === 4 / 3 ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() => setAspect(4 / 3)}
                    >
                      4:3
                    </button>
                  </div>
                </div>

                {/* Zoom slider */}
                <div className="mb-3">
                  <label className="form-label">Zoom</label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="form-range"
                    aria-label="Zoom"
                    style={{ accentColor: "#0d6efd" }}
                  />
                  <div className="d-flex justify-content-between small text-muted">
                    <span>Min</span>
                    <span>{zoom.toFixed(2)}x</span>
                    <span>Max</span>
                  </div>
                </div>

                {/* Rotation & Flip */}
                <div className="mb-3">
                  <label className="form-label mb-1">Transform</label>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm flex-fill"
                      onClick={rotate90}
                    >
                      Rotate 90°
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary btn-sm flex-fill ${
                        flip.horizontal ? "active" : ""
                      }`}
                      onClick={toggleFlipH}
                    >
                      Flip H
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary btn-sm flex-fill ${
                        flip.vertical ? "active" : ""
                      }`}
                      onClick={toggleFlipV}
                    >
                      Flip V
                    </button>
                  </div>
                </div>

                {/* Preview & actions */}
                <div className="mt-4">
                  <div className="d-flex align-items-center mb-2">
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        overflow: "hidden",
                        borderRadius: 8,
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <img
                        src={imageForCrop}
                        alt="thumb"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div className="ms-2">
                      <div className="small text-muted">Original</div>
                      <div className="fw-semibold small">
                        {imageForCrop ? "Loaded" : "No image"}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-secondary btn-sm flex-fill"
                        // onClick={() => {
                        //   setShowCrop(false);
                        //   setImagePreview(imageForCrop);
                        //   if (typeof setBase64Value === "function") {
                        //     setBase64Value(imageForCrop, "");
                        //   }
                        // }}
                        onClick={async () => {
                          setShowCrop(false);
                          setImagePreview(imageForCrop);

                          // Convert base64 → File (JPEG)
                          const res = await fetch(imageForCrop);
                          const blob = await res.blob();
                          const originalFile = new File(
                            [blob],
                            "original.jpg",
                            {
                              type: "image/jpeg",
                            }
                          );

                          // Return both base64 + File
                          if (typeof setBase64Value === "function") {
                            setBase64Value(imageForCrop, originalFile);
                          }
                        }}
                      >
                        Use Original
                      </button>

                      <button
                        className="btn btn-success btn-sm flex-fill"
                        onClick={applyCrop}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            Processing...
                          </>
                        ) : (
                          "Apply Crop"
                        )}
                      </button>

                      <button
                        className="btn btn-outline-secondary btn-sm flex-fill"
                        onClick={() => {
                          setShowCrop(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>

                {/* Small hint */}
                <div className="mt-3 small text-muted">
                  Drag inside the image to move crop. Use slider or pinch to
                  zoom. Click & drag crop edges visually.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* small loader (global) */}
      {loading && !showCrop && (
        <div className="mt-2">
          <div className="d-inline-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            />
            <small className="text-muted">Processing image...</small>
          </div>
        </div>
      )}
    </div>
  );
};

ImageCropperFile.propTypes = {
  base64Value: PropTypes.string,
  setBase64Value: PropTypes.func,
  id: PropTypes.string,
};

export default React.memo(ImageCropperFile);
